import React from 'react';
import {
  Academy,
  AgeGroup,
  AcademySport,
  Lesson,
  Term,
  type ProRataPreviewResponse,
} from '@court-sync/shared';
import { Card } from '../ui/Card';
import { Button } from '@/components/ui';
import { Badge } from '../ui/Badge';
import { cn } from '@/lib/utils';
import { isLessonBookable, getLessonBookingReason, isLessonFull } from '../../utils/lessonBooking';
import { getPublicLessonEnrollmentLabel } from '../../utils/publicLessonEnrollmentLabel';
import { lessonDescriptionTextClassName } from '../../utils/lessonDisplay';
import { formatTimeHHMM } from '../../utils/timeFormat';
import { PublicLessonCardImage } from './PublicLessonCardImage';
import { LessonCardDescriptionBlurb } from './LessonCardDescriptionBlurb';
import { SportBadge } from '@/components/sport/SportBadge';
import { resolveAcademySport } from '@/lib/sportPresentation';
import { Calendar, Clock, Mail, Users, ArrowRight } from 'lucide-react';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const shortDateOptions: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function formatDaysOfWeek(daysOfWeek?: number[]): string {
  if (!daysOfWeek || daysOfWeek.length === 0) return '';
  return daysOfWeek.map((d) => DAY_NAMES[d] ?? '').filter(Boolean).join(', ');
}

function getAgeGroupLabel(lesson: Lesson, ageGroups: AgeGroup[]): string | null {
  if (lesson.ageGroupId) {
    const group = ageGroups.find((g) => g.id === lesson.ageGroupId);
    return group?.label ?? null;
  }
  if (lesson.ageGroup && lesson.ageGroup !== 'all') {
    return lesson.ageGroup.charAt(0).toUpperCase() + lesson.ageGroup.slice(1);
  }
  return null;
}

function formatLessonPrice(
  lesson: Lesson,
  proRataPreview?: ProRataPreviewResponse | null
): string {
  if (
    lesson.lessonType === 'group' &&
    proRataPreview?.academyProRataEnabled &&
    proRataPreview.isProRated
  ) {
    return `$${proRataPreview.proRataPrice} (pro-rated)`;
  }
  if (lesson.lessonType === 'camp') {
    const parts: string[] = [];
    if (lesson.campDayPrice != null) parts.push(`$${lesson.campDayPrice}/day`);
    if (lesson.campFullPrice != null) parts.push(`$${lesson.campFullPrice} full`);
    return parts.length > 0 ? parts.join(' · ') : `$${lesson.price}`;
  }
  if (
    lesson.lessonType === 'private' &&
    lesson.pricingType === 'per_session' &&
    lesson.pricePerSession != null
  ) {
    return `$${lesson.pricePerSession} / session`;
  }
  return `$${lesson.price}`;
}

function primaryBookLabel(
  lesson: Lesson,
  isBookable: boolean,
  isFullyBooked: boolean,
  showJoinWaitlist: boolean,
  defaultLabel: string
): string {
  if (isFullyBooked) return showJoinWaitlist ? 'Join Waitlist' : 'Fully Booked';
  if (!isBookable) return 'Not Available';
  if (lesson.isInvitationOnly) return 'Express Interest';
  return defaultLabel;
}

export type PublicLessonBrowseItemProps = {
  lesson: Lesson;
  term: Term | null;
  academy: Academy;
  ageGroups: AgeGroup[];
  academySports: AcademySport[];
  proRataPreview?: ProRataPreviewResponse | null;
  accentColor?: string | null;
  isMultiSport: boolean;
  starterRequestBooking: boolean;
  bookLabel?: string;
  locationName?: string | null;
  formatDate: (
    date: Date | string,
    region?: string | null,
    options?: Intl.DateTimeFormatOptions
  ) => string;
  onBook: () => void;
  onRequestBooking: () => void;
  onContactAcademy: () => void;
  variant: 'grid' | 'list';
  /** Camp cards may link description to hash on same page. */
  descriptionReadMoreTo?: string;
};

export function PublicLessonBrowseItem({
  lesson,
  term,
  academy,
  ageGroups,
  academySports,
  proRataPreview,
  accentColor,
  isMultiSport,
  starterRequestBooking,
  bookLabel = 'Book Lesson',
  locationName,
  formatDate,
  onBook,
  onRequestBooking,
  onContactAcademy,
  variant,
  descriptionReadMoreTo,
}: PublicLessonBrowseItemProps) {
  const isBookable = isLessonBookable(lesson, term);
  const isFullyBooked = isLessonFull(lesson, academy.allowEnrolmentWhenFull);
  const showJoinWaitlist =
    isFullyBooked && !academy.allowEnrolmentWhenFull && academy.waitlistEnabled === true;
  const bookingReason = getLessonBookingReason(lesson, term);
  const lessonSport = resolveAcademySport(academySports, lesson.academySportId);
  const ageGroupLabel = getAgeGroupLabel(lesson, ageGroups);
  const daysLabel = formatDaysOfWeek(lesson.daysOfWeek);
  const priceLabel = formatLessonPrice(lesson, proRataPreview);
  const actionLabel = primaryBookLabel(
    lesson,
    isBookable,
    isFullyBooked,
    showJoinWaitlist,
    bookLabel
  );

  const metaItems: React.ReactNode[] = [];
  if (daysLabel) {
    metaItems.push(
      <span key="days" className="inline-flex items-center gap-1">
        <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {daysLabel}
      </span>
    );
  }
  if (term?.startDate) {
    metaItems.push(
      <span key="starts" className="inline-flex items-center gap-1">
        Starts {formatDate(term.startDate, academy.region, shortDateOptions)}
      </span>
    );
  }
  if (lesson.lessonType === 'camp' && lesson.startDate && lesson.endDate) {
    metaItems.push(
      <span key="camp-dates" className="inline-flex items-center gap-1">
        <Calendar className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {formatDate(lesson.startDate, academy.region)} –{' '}
        {formatDate(lesson.endDate, academy.region)}
      </span>
    );
  }
  metaItems.push(
    <span key="time" className="inline-flex items-center gap-1">
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {formatTimeHHMM(lesson.startTime)} – {formatTimeHHMM(lesson.endTime)}
    </span>
  );
  if (lesson.maxCapacity !== -1) {
    metaItems.push(
      <span key="enrollment" className="inline-flex items-center gap-1">
        <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {getPublicLessonEnrollmentLabel(lesson)}
      </span>
    );
  }

  const badgeItems: React.ReactNode[] = [];
  if (isMultiSport && lessonSport) {
    badgeItems.push(<SportBadge key="sport" sport={lessonSport} />);
  }
  if (lesson.skillLevel && lesson.skillLevel !== 'any') {
    badgeItems.push(
      <Badge key="skill" variant="outline" className="text-xs">
        {lesson.skillLevel}
      </Badge>
    );
  }
  if (ageGroupLabel) {
    badgeItems.push(
      <Badge key="age" variant="outline" className="text-xs">
        {ageGroupLabel}
      </Badge>
    );
  }
  if (locationName) {
    badgeItems.push(
      <Badge key="location" variant="outline" className="text-xs">
        {locationName}
      </Badge>
    );
  }

  const actionButton = starterRequestBooking ? (
    <div className={cn('flex gap-2', variant === 'grid' ? 'flex-col' : 'flex-row flex-wrap justify-end')}>
      <Button
        size={variant === 'list' ? 'sm' : 'default'}
        className={variant === 'grid' ? 'w-full' : undefined}
        onClick={onRequestBooking}
      >
        Request booking
      </Button>
      {variant === 'grid' ? (
        <Button className="w-full" variant="ghost" size="sm" onClick={onContactAcademy}>
          <Mail className="mr-2 h-4 w-4" />
          Contact academy
        </Button>
      ) : null}
    </div>
  ) : (
    <Button
      size={variant === 'list' ? 'sm' : 'default'}
      className={variant === 'grid' ? 'w-full' : 'shrink-0'}
      onClick={onBook}
      disabled={!isBookable || (isFullyBooked && !showJoinWaitlist)}
      title={!isBookable ? bookingReason || 'Cannot book this lesson' : undefined}
    >
      {actionLabel}
    </Button>
  );

  if (variant === 'list') {
    return (
      <li
        id={`lesson-${lesson.id}`}
        className="group scroll-mt-24 overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
      >
        <div className="flex items-stretch">
          <div className="hidden w-36 shrink-0 sm:block">
            <PublicLessonCardImage
              lesson={lesson}
              accentColor={accentColor}
              academySports={academySports}
              className="h-full min-h-[6.5rem] w-full border-0 border-r border-border bg-muted"
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col p-3.5 sm:min-h-[6.5rem] sm:p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-2">
                <h3 className="text-base font-semibold leading-snug text-foreground">
                  {lesson.name}
                </h3>
                {lesson.description && !descriptionReadMoreTo ? (
                  <p
                    className={cn(
                      'line-clamp-1 text-xs text-muted-foreground',
                      lessonDescriptionTextClassName
                    )}
                  >
                    {lesson.description}
                  </p>
                ) : null}
                {descriptionReadMoreTo ? (
                  <LessonCardDescriptionBlurb
                    description={lesson.description}
                    readMoreTo={descriptionReadMoreTo}
                    className="text-xs"
                  />
                ) : null}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {metaItems.map((item, index) => (
                    <React.Fragment key={index}>
                      {index > 0 ? <span aria-hidden className="text-border">·</span> : null}
                      {item}
                    </React.Fragment>
                  ))}
                </div>
                {badgeItems.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">{badgeItems}</div>
                ) : null}
              </div>
              <span className="shrink-0 text-sm font-semibold text-foreground whitespace-nowrap">
                {priceLabel}
              </span>
            </div>
            <div className="mt-auto flex justify-end pt-3">{actionButton}</div>
          </div>
        </div>
      </li>
    );
  }

  return (
    <Card
      id={`lesson-${lesson.id}`}
      className="group scroll-mt-24 overflow-hidden rounded-2xl border-border shadow-sm transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5"
    >
      <div
        className="h-0.5 bg-gradient-to-r from-primary to-primary/60 opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden
      />
      <PublicLessonCardImage
        lesson={lesson}
        accentColor={accentColor}
        academySports={academySports}
        className="aspect-[2.2/1] w-full overflow-hidden border-b border-border bg-muted sm:aspect-[2.4/1]"
      />
      <div className="space-y-2.5 p-4">
        <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground">
          {lesson.name}
        </h3>
        {descriptionReadMoreTo ? (
          <LessonCardDescriptionBlurb
            description={lesson.description}
            readMoreTo={descriptionReadMoreTo}
            className="text-xs"
          />
        ) : lesson.description ? (
          <p
            className={cn(
              'line-clamp-2 text-xs text-muted-foreground',
              lessonDescriptionTextClassName
            )}
          >
            {lesson.description}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-muted-foreground">
          {metaItems.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 ? <span aria-hidden className="text-border">·</span> : null}
              {item}
            </React.Fragment>
          ))}
        </div>
        {badgeItems.length > 0 ? <div className="flex flex-wrap gap-1.5">{badgeItems}</div> : null}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-border/60 bg-muted/30 px-4 py-3">
        <span className="text-base font-semibold text-foreground">{priceLabel}</span>
        {starterRequestBooking ? (
          <div className="flex min-w-0 flex-1 flex-col items-end gap-2">{actionButton}</div>
        ) : (
          <Button
            size="sm"
            onClick={onBook}
            disabled={!isBookable || (isFullyBooked && !showJoinWaitlist)}
            title={!isBookable ? bookingReason || 'Cannot book this lesson' : undefined}
          >
            {actionLabel}
            <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
          </Button>
        )}
      </div>
    </Card>
  );
}
